/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { object } from 'prop-types';
import MaskedInput from 'react-text-mask';
import { withStyles } from '@material-ui/core/styles';
import {
    Typography,
    Grid,
    Paper,
    TextField,
    NativeSelect,
    Input,
} from '@material-ui/core';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { useFormContext, Controller } from 'react-hook-form';
import { getFeatureFlag } from '../../../../state/ducks/Checkout/ducks/Global/Global-Selectors';
import { getOccasionList, isOccasionHoliday, getDefaultOccasion } from './Helpers';

const styles = () => ({
    paperContainer: {
        backgroundColor: '#F6EFE8',
        minHeight: '378px',
        maxWidth: '700px',
        padding: '20px',
        '@media (min-width:767px)': {
            minHeight: '310px',
        },
        '& .mbp-account-MuiFilledInput-underline:before, .mbp-account-MuiFilledInput-underline.Mui-error:after, .mbp-account-MuiFilledInput-underline:after, .mbp-account-MuiFilledInput-underline.Mui-focused:after': {
            borderBottom: 'none',
        },
        '& .mbp-account-MuiFilledInput-underline.Mui-error': {
            border: '1px solid red',
        },
        '& .mbp-account-MuiFormLabel-root': {
            color: '#555555',
        },
        '& .mbp-account-MuiFilledInput-root': {
            background: '#FFF',
            border: '1px solid #C4C4C4',
            borderRadius: '3px',
            opacity: '1',
        },
    },
    field: {
        borderRadius: '3px',
        backgroundColor: '#fff',
        '&.date': {
            '&:focus-within': {
                '& label': {
                    transform: 'translate(12px, 10px) scale(0.75)',
                },
            }, 
        },
        '& input, select': {
            fontSize: '16px',
        },
        '& .MuiInputBase-root': {
            height: '56px',
        },
        '& svg': {
            width: '16px',
            height: '16px',
            color: '#000000',
        },
        '& .mbp-account-MuiFormHelperText-root.Mui-error': {
            color: '#FFF',
            backgroundColor: '#ff0000',
            margin: '0px',
            padding: '3px 10px',
            fontSize: '12px !important',
        },
    },
    select: {
        background: '#FFF',
        borderRadius: '3px',
        boxShadow: 'none',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        '& input, & select': {
            color: '#000',
            padding: '0 12px 2px 12px',
            '&:focus, &:active': {
                backgroundColor: '#FFF',
            },
        },
        '& svg': {
            color: '#000000',
            height: '40px',
            width: '40px',
            top: 'calc(50% - 20px)',
        },
    },
    remindersTextContainer: {
        gap: 8,
        fontSize: '16px !important',
        margin: '20px 0px 12px 0px',
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: 'Lato, sans-serif',
        '& svg': {
            height: '15px',
            width: '16px',
            color: '#2F363D',
        },
    },
    yourContactText: {
        fontFamily: 'Lato, sans-serif',
        marginBottom: '10px',
        fontSize: '16px !important',
    },
    typographyError: {
        color: '#FFF',
        backgroundColor: '#ff0000',
        margin: '0px',
        padding: '3px 10px',
        fontSize: '12px !important',
    },
    customError: {
        border: '1px solid red !important',
    },
    smallFont: {
        '& .mbp-account-MuiFormLabel-root': {
            fontSize: '12px !important',
        },
    },
});

const CelebrationsRecipientFormCard = ({ classes, recipient, recipientIndex }) => {
    const manualDefaultHolidayId = useSelector(getFeatureFlag('guest-reminders-default-occasion-id'));
    const methods = useFormContext();
    const { formState: { errors }, setValue, control, trigger, clearErrors } = methods;

    const occasionList = getOccasionList();
    const defaultOccasionIndex = getDefaultOccasion(manualDefaultHolidayId, occasionList);
    const defaultOccasionId = occasionList[defaultOccasionIndex]?.occasion || '';

    // Set default holiday for first occasion on mount if empty
    useEffect(() => {
        if (!recipient.occasions[0].occasion) {
            setValue(`recipients[${recipientIndex}].occasions[0].occasion`, defaultOccasionId);
        }
    }, [defaultOccasionId, setValue, recipientIndex]);

    const getFilteredOccasionList = (listIndex) => {
        let updatedOccasionList = [...occasionList];
        const otherOccasion = recipient.occasions.find((_, index) => index !== listIndex)?.occasion;
        if (otherOccasion) {
            updatedOccasionList = updatedOccasionList.filter((occasion) => occasion?.occasion !== otherOccasion);
        }
        return updatedOccasionList;
    };

    const getOccasionListDropdown = (index) => (
        getFilteredOccasionList(index).map((o) => (
            <option aria-hidden="true" key={o.desc} value={o.occasion}>{o.desc}</option>
        ))
    );

    const handleDateChange = (event) => {
        // Persist the event to prevent it from being null
        event.persist();
        let value = event.target.value;

        // Capture the current cursor position
        const cursorPos = event.target.selectionStart;

        // Allow only numeric values and remove all non-numeric characters except the slash
        value = value.replace(/[^0-9/]/g, '');
        
        // Handle case where the user deletes the slash
        if (value.length === 2 && cursorPos === 3 && value.indexOf('/') === -1) {
            value = value.slice(0, 2);
        }

        // Apply the mask logic for MM/DD format:
        // - Handle the case where single-digit months (e.g., 3) should be prefixed with a 0.
        // - Ensure that we don't add an extra leading zero on deletions.
        if (value.length === 1 && Number(value[0]) > 1) {
            // Allow for '3' -> '03', but don't force the '0' after deletion
            value = '0' + value;
        } else if (value.length === 2 && value[0] === '1' && Number(value[1]) > 2) {
            // Handle 13 to 01/3 properly
            value = '01/' + value[1];
        } else {
            value = value.replace(/^([2-9])$/g, '0$1') // To handle 3 > 03
                .replace(/^(1{1})([3-9]{1})$/g, '0$1/$2') // 13 > 01/3
                .replace(/^0{1,}/g, '0') // To handle 00 > 0
                .replace(/^([0-1]{1}[0-9]{1})([0-9]{1,2}).*/g, '$1/$2'); // To handle 113 > 11/3
        }

        // Update the value and the cursor position
        // Cursor position was breaking without this so I added it for now
        setTimeout(() => {
            // Set the cursor position at the end after formatting
            event.target.selectionStart = event.target.selectionEnd = value.length;
        }, 0);
        return value;
    };

    return (
        <Paper elevation={0} className={classes.paperContainer}>
            <Typography className={classes.yourContactText}>Your Loved One</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Controller
                        name={`recipients[${recipientIndex}].rcpFirstName`}
                        control={control}
                        defaultValue={recipient?.rcpFirstName || ''}
                        render={({ field }) => (
                            <TextField
                                label="First"
                                id="rcpFirstName"
                                fullWidth
                                variant="filled"
                                className={classes.field}
                                value={field.value}
                                onChange={(e) => {
                                    field.onChange(e.target.value);
                                    setValue(`recipients[${recipientIndex}].rcpFirstName`, e.target.value);
                                }}
                                error={!!errors?.recipients?.[recipientIndex]?.rcpFirstName}
                                helperText={errors?.recipients?.[recipientIndex]?.rcpFirstName?.message}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Controller
                        name={`recipients[${recipientIndex}].rcpLastName`}
                        control={control}
                        defaultValue={recipient?.rcpLastName || ''}
                        render={({ field }) => (
                            <TextField
                                label="Last"
                                id="rcpLastName"
                                fullWidth
                                variant="filled"
                                className={classes.field}
                                value={field.value}
                                onChange={(e) => {
                                    field.onChange(e.target.value);
                                    setValue(`recipients[${recipientIndex}].rcpLastName`, e.target.value);
                                }}
                                error={!!errors?.recipients?.[recipientIndex]?.rcpLastName}
                                helperText={errors?.recipients?.[recipientIndex]?.rcpLastName?.message}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Typography component="div" className={classes.remindersTextContainer}>
                <ScheduleIcon />
                Reminders
            </Typography>
            <Grid container spacing={2}>
                {recipient.occasions.map((occasion, occasionIndex) => {
                    const isHolidayOccasion = isOccasionHoliday(occasion.occasion);
                    const isOccasionError = errors?.recipients?.[recipientIndex]?.occasions?.[occasionIndex]?.message?.includes('Occasion');
                    const isOccasionDateError = errors?.recipients?.[recipientIndex]?.occasions?.[occasionIndex]?.message?.includes('Date');
                    const occasionErrorMessage = errors?.recipients?.[recipientIndex]?.occasions?.[occasionIndex]?.message;
                    const defaultValue = occasionIndex === 0 ? defaultOccasionId : '';

                    return (
                        <React.Fragment key={`occasion-${occasionIndex}`}>
                            <Grid item xs={6}>
                                <Controller
                                    name={`recipients[${recipientIndex}].occasions[${occasionIndex}].occasion`}
                                    control={control}
                                    defaultValue={occasion.occasion || defaultValue}
                                    render={({ field }) => (
                                        <NativeSelect
                                            fullWidth
                                            label="Occasion"
                                            disableUnderline
                                            variant="filled"
                                            value={field.value}
                                            style={{ height: '56px' }}
                                            className={`${classes.select} ${classes.field} ${isOccasionError ? classes.customError : ''}`}
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                                if (isOccasionHoliday(e.target.value)) {
                                                    setValue(`recipients[${recipientIndex}].occasions[${occasionIndex}].date`, '');
                                                    clearErrors(`recipients[${recipientIndex}].occasions[${occasionIndex}]`);
                                                }
                                                if (isOccasionError) {
                                                    trigger(`recipients[${recipientIndex}].occasions[${occasionIndex}]`);
                                                }
                                            }}
                                            input={(
                                                <Input
                                                    name={field.name}
                                                    id={`occasion-native-label-placeholder-${recipientIndex}-${occasionIndex}`}
                                                    data-test="occasion-native-label-placeholder"
                                                />
                                            )}
                                        >
                                            {getOccasionListDropdown(occasionIndex)}
                                        </NativeSelect>
                                    )}
                                />
                                {isOccasionError && (
                                    <Typography className={classes.typographyError}>
                                        {occasionErrorMessage}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <Controller
                                    name={`recipients[${recipientIndex}].occasions[${occasionIndex}].date`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            fullWidth
                                            label={isHolidayOccasion ? 'No date needed.' : 'MM/DD'}
                                            value={field.value || ''}
                                            className={`date ${classes.field} ${isHolidayOccasion ? classes.smallFont : ''}`}
                                            variant="filled"
                                            disabled={isHolidayOccasion}
                                            InputProps={{
                                                inputComponent: MaskedInput,
                                                inputProps: {
                                                    mask: [/\d/, /\d/, '/', /\d/, /\d/],
                                                    guide: false,
                                                },
                                            }}
                                            InputLabelProps={{
                                                shrink: !!field.value,
                                            }}
                                            onChange={(e) => {
                                                const formattedDate = handleDateChange(e);
                                                field.onChange(formattedDate);
                                                if (isOccasionDateError) {
                                                    trigger(`recipients[${recipientIndex}].occasions[${occasionIndex}]`);
                                                }
                                            }}
                                            error={isOccasionDateError}
                                            helperText={isOccasionDateError && occasionErrorMessage}
                                        />
                                    )}
                                />
                            </Grid>
                        </React.Fragment>
                    );
                })}
            </Grid>
        </Paper>
    );
};

CelebrationsRecipientFormCard.propTypes = {
    classes: object,
    recipient: object.isRequired,
    featureFlags: object,
};

CelebrationsRecipientFormCard.defaultProps = {
    classes: {},
    featureFlags: {},
};

export default withStyles(styles)(CelebrationsRecipientFormCard);